/**
 * W1-11b (app-shell-ui): the shell's global commands (WORKPACKAGES Appendix B: `app.*`, `edit.undo|redo`, `view.*`,
 * `playback.*`, `snap.*`, `nav.goto|reveal|prevKey|nextKey|prevNote|nextNote`, `help.palette|cheatSheet`) and their
 * key bindings for the Default and RPE presets (DESIGN.md §8.2, §8.3; RPE ids from E§3.20).
 *
 * Every command has a menu or palette path; the frequent ones have keys in both presets. Alt chords declare their
 * Alt-free replacement (or null), chords macOS reserves declare their Mac chord (or null), no Ctrl+Alt chord and no
 * reserved chord is bound (checked by the startup audit, registry.test.ts). Theater and test play are W2-02's.
 */

import type { Json, MessageRef } from '../../core/util/types';
import type { LineId, KeyId, NoteId, TrackId } from '../../core/model/types';
import type { ExperienceLevel, LayoutPreset, WorkspaceId } from '../../state/types';
import { PX_PER_BEAT_MAX, PX_PER_BEAT_MIN, DEFAULT_TIMELINE } from '../../state/viewState';
import { Beats } from '../../core/time/beat';
import type { BeatArg } from '../registry/commands';
import type { CommandContext, CommandDef, KeyBinding, KeymapPresetId } from '../registry/types';
import { isShellUiService, type PalettePrefix } from '../uiService';
import { FOCUS_REGION_COMMANDS, FOCUS_REGION_KEYBINDINGS } from './FocusRegions';
import { ref } from './commandState';
import {
  computeChartLayout,
  effectiveLayout,
  LAYOUT_PRESET_IDS,
  LAYOUT_PRESETS,
  layoutViewport,
  type ChartLayout,
} from './LayoutPresets';
import {
  barStep,
  beatFromArg,
  chartEndSec,
  cycleSnap,
  keyBeatsOf,
  meterOf,
  neighbour,
  noteBeats,
  playheadBeat,
  playheadSec,
  RATE_PRESETS,
  seekBeat,
  seekSec,
  selectionRange,
  SNAP_DIGIT_DIVS,
  snapNearest,
  snapStep,
  stepRate,
  tempoOf,
} from './playback';
import { m } from './shell.messages';

// ---------------------------------------------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------------------------------------------

type Enabled = true | MessageRef;

const WORKSPACE_TITLES: Readonly<Record<WorkspaceId, typeof m.wsChart>> = {
  timing: m.wsTiming,
  chart: m.wsChart,
  publish: m.wsPublish,
};

function projectOpen(ctx: CommandContext): Enabled {
  return ctx.store !== null ? true : ref(m.reasonNoProject);
}

function obj(args: unknown): Readonly<Record<string, unknown>> {
  return typeof args === 'object' && args !== null && !Array.isArray(args) ? (args as Record<string, unknown>) : {};
}

function numberArg(args: unknown, key: string): number | undefined {
  if (typeof args === 'number' && Number.isFinite(args)) return args;
  const v = obj(args)[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
}

function stringArg<T extends string>(args: unknown, key: string, allowed: readonly T[]): T | undefined {
  const v = typeof args === 'string' ? args : obj(args)[key];
  return typeof v === 'string' && (allowed as readonly string[]).includes(v) ? (v as T) : undefined;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

function viewport(): { width: number; height: number } {
  if (typeof window === 'undefined') return { width: 1536, height: 730 };
  return { width: window.innerWidth, height: window.innerHeight };
}

/** The layout preset in effect now (Laptop on small screens). */
export function currentLayout(ctx: Pick<CommandContext, 'settings'>): LayoutPreset {
  const g = ctx.settings.value;
  return effectiveLayout(g.editing.layout, layoutViewport(viewport(), g.general.uiScale));
}

/** The Chart workspace layout for the window now (placement of the preview, dock and inspector). */
export function currentChartLayout(ctx: Pick<CommandContext, 'settings'>): ChartLayout {
  const g = ctx.settings.value;
  return computeChartLayout({
    viewport: layoutViewport(viewport(), g.general.uiScale),
    preset: g.editing.layout,
    level: g.general.level,
  });
}

function level(ctx: Pick<CommandContext, 'settings'>): ExperienceLevel {
  return ctx.settings.value.general.level;
}

/** Panel id of the Problems tab (W2-12a registers it in the dock). */
export function problemsPanelId(ctx: Pick<CommandContext, 'registry'>): string | null {
  const dock = ctx.registry.panels('chart.dock');
  return (dock.find((p) => p.id === 'problems') ?? dock.find((p) => /problem/i.test(p.id)))?.id ?? null;
}

function zoomBy(ctx: CommandContext, factor: number): void {
  const tl = ctx.editor.getState().timeline;
  const next = clamp(tl.pxPerBeat * factor, PX_PER_BEAT_MIN, PX_PER_BEAT_MAX);
  if (next === tl.pxPerBeat) return;
  const host = ctx.timeline;
  if (host !== null) host.setPxPerBeat(next, playheadBeat(ctx));
  else ctx.editor.getState().setTimeline({ pxPerBeat: next });
}

/** Undo or redo one step, with the toast and "jump to the change" (settings.editing.jumpToChangeOnUndo). */
function historyStep(ctx: CommandContext, dir: 'undo' | 'redo'): void {
  const store = ctx.store;
  if (store === null) return;
  const h = store.history;
  const entries = h.entries();
  const entry = dir === 'undo' ? entries[h.position] : entries[h.position + 1];
  const txn = dir === 'undo' ? store.undo() : store.redo();
  if (txn === null) return;
  const label = entry === undefined ? '' : ctx.i18n.tr(entry.label);
  ctx.ui.toast(ref(dir === 'undo' ? m.toastUndid : m.toastRedid, { label }), { kind: 'info', durationMs: 2500 });
  if (!ctx.settings.value.editing.jumpToChangeOnUndo) return;
  const focus = txn.focus;
  if (focus?.line !== undefined && store.doc.lines.has(focus.line)) ctx.editor.getState().setActiveLine(focus.line);
  if (focus?.beat !== undefined) {
    try {
      ctx.timeline?.reveal(Beats.toF64(focus.beat), 'center');
    } catch {
      // The timeline may be unmounted (another workspace).
    }
  }
}

function historyEnabled(ctx: CommandContext, dir: 'undo' | 'redo'): Enabled {
  const store = ctx.store;
  if (store === null) return ref(m.reasonNoProject);
  if (store.gestureActive) return ref(m.reasonGestureActive);
  if (dir === 'undo' ? !store.history.canUndo : !store.history.canRedo) {
    return ref(dir === 'undo' ? m.reasonNothingToUndo : m.reasonNothingToRedo);
  }
  return true;
}

async function play(
  ctx: CommandContext,
  opts: Parameters<CommandContext['audio']['transport']['play']>[0],
): Promise<void> {
  await ctx.audio.ensureContext();
  await ctx.audio.transport.play(opts);
}

function setRate(ctx: CommandContext, rate: number): void {
  const r = clamp(rate, 0.05, 4);
  ctx.editor.getState().setRate(r);
  ctx.audio.transport.setRate(r);
}

/** The layout preset in effect differs from the user's choice (Laptop applied automatically on a small screen). */
export function layoutIsAuto(ctx: Pick<CommandContext, 'settings'>): boolean {
  return currentLayout(ctx) !== ctx.settings.value.editing.layout;
}

/** Choice prompt for a command run without its argument from a menu (says when Laptop is applied automatically). */
async function chooseLayout(ctx: CommandContext): Promise<LayoutPreset | null> {
  return ctx.ui.choose<LayoutPreset>({
    title: ref(m.cmdLayout),
    ...(layoutIsAuto(ctx) ? { body: ref(m.layoutAuto) } : {}),
    options: LAYOUT_PRESET_IDS.map((id) => ({
      value: id,
      label: ref(LAYOUT_PRESETS[id].title),
      description: ref(LAYOUT_PRESETS[id].description),
    })),
  });
}

/** Remembered loop region per project (Alt+L brings it back after turning the loop off). */
const lastLoop = new Map<string, NonNullable<ReturnType<CommandContext['editor']['getState']>['loop']>>();

function seekNeighbour(ctx: CommandContext, list: readonly number[], dir: 1 | -1, none: MessageRef): void {
  const target = neighbour(list, playheadBeat(ctx), dir);
  if (target === null) {
    ctx.ui.toast(none, { kind: 'info', durationMs: 2500 });
    return;
  }
  seekBeat(ctx, target);
}

function tracksOfLine(ctx: CommandContext, line: LineId): TrackId[] {
  const store = ctx.store;
  if (store === null) return [];
  const out: TrackId[] = [];
  for (const list of store.idx.tracksOfLine(line).values()) out.push(...list);
  return out;
}

// ---------------------------------------------------------------------------------------------------------------
// Commands
// ---------------------------------------------------------------------------------------------------------------

const PALETTE_PREFIXES: readonly PalettePrefix[] = ['>', '#', '@', '?', ':', '='];

const appCommands: CommandDef[] = [
  {
    id: 'app.palette',
    title: m.cmdPalette,
    category: 'Help',
    icon: 'Search',
    aliases: ['command palette', 'search', 'find command', 'actions', '命令', '搜索'],
    level: 'guided',
    hint: 6,
    menus: [{ menu: 'app.help', group: 'find', order: 10 }],
    run(ctx, args) {
      ctx.ui.openPalette(stringArg(args, 'prefix', PALETTE_PREFIXES) ?? '>');
    },
  },
  {
    id: 'app.workspace',
    title: m.cmdWorkspace,
    category: 'View',
    icon: 'PanelsTopLeft',
    aliases: ['workspace', 'tab', 'timing', 'chart', 'publish', 'song', '工作区'],
    level: 'guided',
    args: {
      kind: 'choice',
      label: ref(m.argWorkspace),
      options: (['timing', 'chart', 'publish'] as const).map((w) => ({ value: w, label: ref(WORKSPACE_TITLES[w]) })),
    },
    enabled: projectOpen,
    menus: [{ menu: 'app.view', group: 'workspace', order: 10 }],
    async run(ctx, args) {
      const ws =
        stringArg<WorkspaceId>(args, 'workspace', ['timing', 'chart', 'publish']) ??
        (await ctx.ui.choose<WorkspaceId>({
          title: ref(m.cmdWorkspace),
          options: (['timing', 'chart', 'publish'] as const).map((w) => ({
            value: w,
            label: ref(WORKSPACE_TITLES[w]),
          })),
        }));
      if (ws !== null) ctx.nav.setWorkspace(ws);
    },
  },
  {
    id: 'app.library',
    title: m.cmdLibrary,
    category: 'File',
    icon: 'LibraryBig',
    aliases: ['library', 'home', 'my charts', 'projects', '谱面库'],
    level: 'guided',
    menus: [{ menu: 'app.project', group: 'close', order: 990 }],
    run(ctx) {
      ctx.nav.goLibrary();
    },
  },
  {
    id: 'app.closeProject',
    title: m.cmdCloseProject,
    category: 'File',
    icon: 'LibraryBig',
    aliases: ['close', 'library', 'home', 'back', 'exit', '关闭', '谱面库'],
    level: 'guided',
    enabled: projectOpen,
    menus: [{ menu: 'app.project', group: 'close', order: 1000 }],
    async run(ctx) {
      await ctx.nav.closeProject();
    },
  },
];

const editCommands: CommandDef[] = [
  {
    id: 'edit.undo',
    title: m.cmdUndo,
    category: 'Edit',
    icon: 'Undo2',
    aliases: ['undo', 'revert', 'back', '撤销', '撤回'],
    level: 'guided',
    repeat: true,
    hint: 2,
    enabled: (ctx) => historyEnabled(ctx, 'undo'),
    menus: [{ menu: 'app.edit', group: 'history', order: 10 }],
    run(ctx) {
      historyStep(ctx, 'undo');
    },
  },
  {
    id: 'edit.redo',
    title: m.cmdRedo,
    category: 'Edit',
    icon: 'Redo2',
    aliases: ['redo', 'repeat', '重做'],
    level: 'guided',
    repeat: true,
    enabled: (ctx) => historyEnabled(ctx, 'redo'),
    menus: [{ menu: 'app.edit', group: 'history', order: 20 }],
    run(ctx) {
      historyStep(ctx, 'redo');
    },
  },
];

const viewCommands: CommandDef[] = [
  ...FOCUS_REGION_COMMANDS,
  {
    id: 'view.zoomIn',
    title: m.cmdZoomIn,
    category: 'View',
    icon: 'ZoomIn',
    aliases: ['zoom', 'magnify', 'stretch time', '放大'],
    level: 'guided',
    repeat: true,
    enabled: projectOpen,
    menus: [{ menu: 'app.view', group: 'zoom', order: 10 }],
    run(ctx) {
      zoomBy(ctx, 1.25);
    },
  },
  {
    id: 'view.zoomOut',
    title: m.cmdZoomOut,
    category: 'View',
    icon: 'ZoomOut',
    aliases: ['zoom', 'shrink time', '缩小'],
    level: 'guided',
    repeat: true,
    enabled: projectOpen,
    menus: [{ menu: 'app.view', group: 'zoom', order: 20 }],
    run(ctx) {
      zoomBy(ctx, 1 / 1.25);
    },
  },
  {
    id: 'view.resetView',
    title: m.cmdResetView,
    category: 'View',
    icon: 'Maximize',
    aliases: ['reset zoom', 'default view', 'zoom 100', 'pan'],
    level: 'guided',
    enabled: projectOpen,
    menus: [{ menu: 'app.view', group: 'zoom', order: 30 }],
    run(ctx) {
      const e = ctx.editor.getState();
      e.setTimeline({
        pxPerBeat: DEFAULT_TIMELINE.pxPerBeat,
        laneHalfRange: DEFAULT_TIMELINE.laneHalfRange,
        laneCenterX: DEFAULT_TIMELINE.laneCenterX,
        followPlayhead: true,
      });
      ctx.timeline?.setPxPerBeat(DEFAULT_TIMELINE.pxPerBeat, playheadBeat(ctx));
    },
  },
  {
    // RPE VIEW-05 (Ctrl+M in the RPE preset): the lane's X centre goes back to 0; the X zoom and time view stay.
    id: 'view.resetLanePan',
    title: m.cmdResetLanePan,
    category: 'View',
    icon: 'MoveHorizontal',
    aliases: ['reset lane pan', 'reset lane x', 'centre lane', 'center lane', 'vision pan', 'reset vision'],
    level: 'guided',
    enabled: projectOpen,
    menus: [{ menu: 'app.view', group: 'zoom', order: 35 }],
    run(ctx) {
      ctx.editor.getState().setTimeline({ laneCenterX: DEFAULT_TIMELINE.laneCenterX });
    },
  },
  {
    id: 'view.frameSelection',
    title: m.cmdFrameSelection,
    category: 'View',
    icon: 'Scan',
    aliases: ['frame', 'fit', 'zoom to selection', 'focus selection'],
    level: 'guided',
    enabled: (ctx) =>
      ctx.store === null ? ref(m.reasonNoProject) : selectionRange(ctx) === null ? ref(m.reasonNoSelection) : true,
    menus: [{ menu: 'app.view', group: 'zoom', order: 40 }],
    run(ctx) {
      const range = selectionRange(ctx);
      if (range === null) return;
      const host = ctx.timeline;
      const height = host?.axis.heightPx ?? 600;
      const span = Math.max(range.end - range.start, 0.25);
      const px = clamp((height * 0.7) / span, PX_PER_BEAT_MIN, PX_PER_BEAT_MAX);
      const mid = (range.start + range.end) / 2;
      if (host !== null) {
        host.setPxPerBeat(px, mid);
        host.reveal(mid, 'center');
      } else {
        ctx.editor
          .getState()
          .setTimeline({ pxPerBeat: px, bottomBeat: Math.max(0, mid - height / 2 / px), followPlayhead: false });
      }
    },
  },
  {
    id: 'view.motionColumns',
    title: m.cmdMotionColumns,
    category: 'View',
    icon: 'Columns3',
    aliases: ['motion columns', 'events', 'note only', 'co-edit', '事件'],
    level: 'guided',
    enabled: projectOpen,
    checked: (ctx) => ctx.editor.getState().timeline.showMotionColumns,
    menus: [{ menu: 'app.view', group: 'timeline', order: 10 }],
    run(ctx) {
      const e = ctx.editor.getState();
      e.setTimeline({ showMotionColumns: !e.timeline.showMotionColumns });
    },
  },
  {
    id: 'view.waveform',
    title: m.cmdWaveform,
    category: 'View',
    icon: 'AudioWaveform',
    aliases: ['waveform', 'audio', 'spectrum', '波形'],
    level: 'guided',
    enabled: projectOpen,
    checked: (ctx) => ctx.editor.getState().timeline.waveform,
    menus: [{ menu: 'app.view', group: 'timeline', order: 20 }],
    run(ctx) {
      const e = ctx.editor.getState();
      e.setTimeline({ waveform: !e.timeline.waveform });
    },
  },
  {
    id: 'view.followPlayhead',
    title: m.cmdFollowPlayhead,
    category: 'View',
    icon: 'LocateFixed',
    aliases: ['follow', 'auto scroll', 'track playhead'],
    level: 'guided',
    enabled: projectOpen,
    checked: (ctx) => ctx.editor.getState().timeline.followPlayhead,
    menus: [{ menu: 'app.view', group: 'timeline', order: 30 }],
    run(ctx) {
      const e = ctx.editor.getState();
      e.setTimeline({ followPlayhead: !e.timeline.followPlayhead });
    },
  },
  {
    id: 'view.layout',
    title: m.cmdLayout,
    category: 'View',
    icon: 'LayoutDashboard',
    aliases: ['layout', 'compose', 'animate', 'review', 'laptop', 'preset', '布局'],
    level: 'guided',
    args: {
      kind: 'choice',
      label: ref(m.argLayout),
      options: LAYOUT_PRESET_IDS.map((id) => ({ value: id, label: ref(LAYOUT_PRESETS[id].title) })),
    },
    menus: [{ menu: 'app.view', group: 'layout', order: 10 }],
    async run(ctx, args) {
      const preset = stringArg<LayoutPreset>(args, 'layout', LAYOUT_PRESET_IDS) ?? (await chooseLayout(ctx));
      if (preset === null) return;
      // The workspace layout opens the preset's dock tab (Review: Problems) when the preset takes effect.
      ctx.settings.set('editing', 'layout', preset);
      ctx.editor.getState().setLayout(preset);
    },
  },
  {
    id: 'view.miniPreview',
    title: m.cmdMiniPreview,
    category: 'View',
    icon: 'PictureInPicture2',
    aliases: ['mini preview', 'floating preview', 'picture in picture', 'pip'],
    level: 'guided',
    enabled: (ctx) => {
      if (ctx.store === null) return ref(m.reasonNoProject);
      if (currentLayout(ctx) !== 'laptop') return ref(m.reasonLaptopOnly);
      if (level(ctx) === 'guided') return ref(m.reasonGuidedDocked);
      if (currentChartLayout(ctx).previewPlacement !== 'floating') return ref(m.reasonNarrowDocked);
      return true;
    },
    checked: (ctx) => isShellUiService(ctx.ui) && ctx.ui.getSnapshot().miniPreview,
    menus: [{ menu: 'app.view', group: 'laptop', order: 10 }],
    run(ctx) {
      if (isShellUiService(ctx.ui)) ctx.ui.setMiniPreview(!ctx.ui.getSnapshot().miniPreview);
    },
  },
  {
    id: 'view.drawer',
    title: m.cmdDrawer,
    category: 'View',
    icon: 'PanelRight',
    aliases: ['drawer', 'inspector', 'side panel', 'dock'],
    level: 'guided',
    enabled: (ctx) => {
      if (ctx.store === null) return ref(m.reasonNoProject);
      return currentLayout(ctx) === 'laptop' ? true : ref(m.reasonLaptopOnly);
    },
    checked: (ctx) => isShellUiService(ctx.ui) && ctx.ui.getSnapshot().drawer,
    menus: [{ menu: 'app.view', group: 'laptop', order: 20 }],
    run(ctx) {
      if (isShellUiService(ctx.ui)) ctx.ui.setDrawer(!ctx.ui.getSnapshot().drawer);
    },
  },
];

const RATE_OPTIONS = RATE_PRESETS.map((r) => ({ value: r, label: ref(m.rateOption, { rate: r.toFixed(2) }) }));

/**
 * UX-055 (CR-W3-06-8): after a stop that returns the playhead to where playback started, scroll the timeline to the
 * restored time — the host only follows while playing (`applyFollow` returns when `!playing`), so without this the
 * lane and motion columns stay at the old scroll until Home.
 */
function revealReturn(ctx: CommandContext, sec: number): void {
  const store = ctx.store;
  if (store === null) return;
  try {
    ctx.timeline?.reveal(tempoOf(store).beatF64At(Math.max(0, sec)), 'playhead');
  } catch {
    // The timeline may be mid-mount; the playhead still moved.
  }
}

const playbackCommands: CommandDef[] = [
  {
    id: 'playback.toggle',
    title: m.cmdPlayPause,
    category: 'Playback',
    icon: 'Play',
    aliases: ['play', 'pause', 'stop', 'start', '播放', '暂停'],
    level: 'guided',
    hint: 9,
    enabled: projectOpen,
    menus: [{ menu: 'app.view', group: 'playback', order: 10 }],
    async run(ctx) {
      const t = ctx.audio.transport;
      if (t.state.playing) {
        const returnToStart = ctx.editor.getState().returnOnStop;
        t.stop({ returnToStart });
        if (returnToStart) revealReturn(ctx, t.chartTime());
        return;
      }
      await play(ctx, { mode: 'flow' });
    },
  },
  {
    id: 'playback.playFromStart',
    title: m.cmdPlayFromStart,
    category: 'Playback',
    icon: 'SkipBack',
    aliases: ['replay', 'from start', 'restart', 'beginning'],
    level: 'guided',
    enabled: projectOpen,
    menus: [{ menu: 'app.view', group: 'playback', order: 20 }],
    async run(ctx) {
      await play(ctx, { from: 0, mode: 'flow' });
    },
  },
  {
    id: 'playback.holdReturn',
    title: m.cmdHoldReturn,
    category: 'Playback',
    icon: 'Undo',
    aliases: ['hold to play', 'audition', 'preview', 'NAV-10'],
    level: 'guided',
    kind: 'hold',
    hint: 4,
    enabled: projectOpen,
    async run(ctx) {
      await play(ctx, { mode: ctx.source === 'key' ? 'holdReturn' : 'flow' });
    },
    release(ctx) {
      const t = ctx.audio.transport;
      if (t.state.playing) {
        t.stop({ returnToStart: true });
        revealReturn(ctx, t.chartTime());
      }
    },
  },
  {
    id: 'playback.holdStay',
    title: m.cmdHoldStay,
    category: 'Playback',
    icon: 'Footprints',
    aliases: ['hold to play', 'audition', 'NAV-11'],
    level: 'guided',
    kind: 'hold',
    enabled: projectOpen,
    async run(ctx) {
      await play(ctx, { mode: ctx.source === 'key' ? 'holdStay' : 'flow' });
    },
    release(ctx) {
      const t = ctx.audio.transport;
      if (t.state.playing) t.stop({ returnToStart: false });
    },
  },
  {
    id: 'playback.stopReturn',
    title: m.cmdStopReturn,
    category: 'Playback',
    icon: 'SquareArrowLeft',
    aliases: ['stop', 'return', 'NAV-07'],
    level: 'guided',
    enabled: projectOpen,
    run(ctx) {
      const t = ctx.audio.transport;
      if (t.state.playing) {
        t.stop({ returnToStart: true });
        revealReturn(ctx, t.chartTime());
      } else seekSec(ctx, t.state.startChartTime);
    },
  },
  {
    id: 'playback.stopStay',
    title: m.cmdStopStay,
    category: 'Playback',
    icon: 'Square',
    aliases: ['stop', 'leap', 'pause here', 'NAV-08'],
    level: 'guided',
    enabled: projectOpen,
    run(ctx) {
      const t = ctx.audio.transport;
      if (t.state.playing) t.stop({ returnToStart: false });
    },
  },
  {
    id: 'playback.playMaximized',
    title: m.cmdPlayMaximized,
    category: 'Playback',
    icon: 'Maximize2',
    aliases: ['play maximised', 'full preview', 'NAV-06'],
    level: 'full',
    enabled: projectOpen,
    async run(ctx) {
      if (!ctx.editor.getState().preview.theater) {
        if (ctx.registry.command('preview.theater') !== undefined)
          await ctx.registry.run('preview.theater', undefined, ctx.source);
        else ctx.editor.getState().setPreview({ theater: true });
      }
      await play(ctx, { mode: 'flow' });
    },
  },
  {
    id: 'playback.returnOnStop',
    title: m.cmdReturnOnStop,
    category: 'Playback',
    icon: 'Repeat',
    aliases: ['return to start', 'space stops', 'go back on stop'],
    level: 'guided',
    checked: (ctx) => ctx.editor.getState().returnOnStop,
    menus: [{ menu: 'app.view', group: 'playback', order: 30 }],
    run(ctx) {
      const on = !ctx.editor.getState().returnOnStop;
      ctx.settings.set('audio', 'spaceStops', on ? 'return' : 'stay');
      ctx.editor.getState().setReturnOnStop(on);
    },
  },
  {
    id: 'playback.rateDown',
    title: m.cmdRateDown,
    category: 'Playback',
    icon: 'Rewind',
    aliases: ['slower', 'speed down', 'rate', '减速'],
    level: 'guided',
    enabled: projectOpen,
    run(ctx) {
      setRate(ctx, stepRate(ctx.editor.getState().rate, -1));
    },
  },
  {
    id: 'playback.rateUp',
    title: m.cmdRateUp,
    category: 'Playback',
    icon: 'FastForward',
    aliases: ['faster', 'speed up', 'rate', '加速'],
    level: 'guided',
    enabled: projectOpen,
    run(ctx) {
      setRate(ctx, stepRate(ctx.editor.getState().rate, 1));
    },
  },
  {
    id: 'playback.setRate',
    title: m.cmdSetRate,
    category: 'Playback',
    icon: 'Gauge',
    aliases: ['rate', 'speed', 'slow down', 'NAV-12', '倍速'],
    level: 'guided',
    args: { kind: 'choice', label: ref(m.argRate), options: RATE_OPTIONS },
    enabled: projectOpen,
    run(ctx, args) {
      const r = numberArg(args, 'rate');
      if (r !== undefined && r > 0) setRate(ctx, r);
    },
  },
  {
    id: 'playback.metronome',
    title: m.cmdMetronome,
    category: 'Playback',
    icon: 'Drum',
    aliases: ['metronome', 'click', 'beat clicks', '节拍器'],
    level: 'guided',
    enabled: projectOpen,
    checked: (ctx) => ctx.editor.getState().metronome,
    menus: [{ menu: 'app.view', group: 'playback', order: 40 }],
    run(ctx) {
      const e = ctx.editor.getState();
      e.setMetronome(!e.metronome);
    },
  },
  {
    id: 'playback.loop',
    title: m.cmdLoop,
    category: 'Playback',
    icon: 'Repeat2',
    aliases: ['loop', 'repeat section', 'cycle', '循环'],
    level: 'guided',
    enabled: projectOpen,
    checked: (ctx) => ctx.editor.getState().loop !== null,
    menus: [{ menu: 'app.view', group: 'playback', order: 50 }],
    run(ctx) {
      const store = ctx.store;
      if (store === null) return;
      const e = ctx.editor.getState();
      const key = store.projectId;
      if (e.loop !== null) {
        lastLoop.set(key, e.loop);
        e.setLoop(null);
        return;
      }
      const div = e.snap.div;
      const range = selectionRange(ctx);
      if (range !== null && range.end - range.start > 1e-9) {
        e.setLoop({
          start: Beats.floorTo(Beats.fromF64(range.start, 1920), div),
          end: Beats.ceilTo(Beats.fromF64(range.end, 1920), div),
        });
        return;
      }
      const remembered = lastLoop.get(key);
      if (remembered !== undefined) {
        e.setLoop(remembered);
        return;
      }
      // The bar under the playhead.
      const mm = meterOf(store);
      const start = mm.barStartAt(Beats.fromF64(Math.max(0, playheadBeat(ctx)), 1920));
      e.setLoop({ start, end: mm.nextBarStart(start) });
    },
  },
  {
    id: 'playback.seekForward',
    title: m.cmdSeekForward,
    category: 'Playback',
    icon: 'ChevronUp',
    aliases: ['step forward', 'next beat', 'seek'],
    level: 'guided',
    repeat: true,
    hint: 1,
    enabled: projectOpen,
    run(ctx) {
      seekBeat(ctx, snapStep(playheadBeat(ctx), ctx.editor.getState().snap.div, 1));
    },
  },
  {
    id: 'playback.seekBack',
    title: m.cmdSeekBack,
    category: 'Playback',
    icon: 'ChevronDown',
    aliases: ['step back', 'previous beat', 'seek'],
    level: 'guided',
    repeat: true,
    enabled: projectOpen,
    run(ctx) {
      seekBeat(ctx, Math.max(0, snapStep(playheadBeat(ctx), ctx.editor.getState().snap.div, -1)));
    },
  },
  {
    id: 'playback.seekBarForward',
    title: m.cmdSeekBarForward,
    category: 'Playback',
    icon: 'ChevronsUp',
    aliases: ['next bar', 'measure', 'page'],
    level: 'guided',
    repeat: true,
    enabled: projectOpen,
    run(ctx) {
      if (ctx.store !== null) seekBeat(ctx, barStep(ctx.store, playheadBeat(ctx), 1));
    },
  },
  {
    id: 'playback.seekBarBack',
    title: m.cmdSeekBarBack,
    category: 'Playback',
    icon: 'ChevronsDown',
    aliases: ['previous bar', 'measure', 'page'],
    level: 'guided',
    repeat: true,
    enabled: projectOpen,
    run(ctx) {
      if (ctx.store !== null) seekBeat(ctx, Math.max(0, barStep(ctx.store, playheadBeat(ctx), -1)));
    },
  },
  {
    id: 'playback.seekStart',
    title: m.cmdSeekStart,
    category: 'Playback',
    icon: 'ArrowDownToLine',
    aliases: ['start', 'beginning', 'home', 'rewind'],
    level: 'guided',
    enabled: projectOpen,
    menus: [{ menu: 'app.view', group: 'playback', order: 60 }],
    run(ctx) {
      seekSec(ctx, 0);
    },
  },
  {
    id: 'playback.seekEnd',
    title: m.cmdSeekEnd,
    category: 'Playback',
    icon: 'ArrowUpToLine',
    aliases: ['end', 'finish', 'last'],
    level: 'guided',
    enabled: projectOpen,
    run(ctx) {
      seekSec(ctx, chartEndSec(ctx));
    },
  },
  {
    id: 'playback.seekSelectionStart',
    title: m.cmdSeekSelStart,
    category: 'Playback',
    icon: 'ArrowDownFromLine',
    aliases: ['selection start', 'jump to start', 'NAV-14'],
    level: 'guided',
    enabled: (ctx) =>
      ctx.store === null ? ref(m.reasonNoProject) : selectionRange(ctx) === null ? ref(m.reasonNoSelection) : true,
    run(ctx) {
      const r = selectionRange(ctx);
      if (r !== null) seekBeat(ctx, r.start);
    },
  },
  {
    id: 'playback.seekSelectionEnd',
    title: m.cmdSeekSelEnd,
    category: 'Playback',
    icon: 'ArrowUpFromLine',
    aliases: ['selection end', 'jump to end', 'NAV-14'],
    level: 'guided',
    enabled: (ctx) =>
      ctx.store === null ? ref(m.reasonNoProject) : selectionRange(ctx) === null ? ref(m.reasonNoSelection) : true,
    run(ctx) {
      const r = selectionRange(ctx);
      if (r !== null) seekBeat(ctx, r.end);
    },
  },
  {
    id: 'playback.seekSeconds',
    title: m.cmdSeekSeconds,
    category: 'Playback',
    icon: 'Timer',
    aliases: ['seek', 'skip', 'nudge time'],
    level: 'full',
    repeat: true,
    args: { kind: 'number', label: ref(m.argSeconds), min: -600, max: 600 },
    enabled: projectOpen,
    run(ctx, args) {
      const d = numberArg(args, 'sec');
      if (d !== undefined) seekSec(ctx, playheadSec(ctx) + d);
    },
  },
  {
    id: 'playback.snapPlayhead',
    title: m.cmdSnapPlayhead,
    category: 'Playback',
    icon: 'Magnet',
    aliases: ['snap time', 'align playhead', 'NAV-04'],
    level: 'guided',
    enabled: projectOpen,
    run(ctx) {
      seekBeat(ctx, snapNearest(playheadBeat(ctx), ctx.editor.getState().snap.div));
    },
  },
];

const snapCommands: CommandDef[] = [
  {
    id: 'snap.setDiv',
    title: m.cmdSnapDiv,
    category: 'Edit',
    icon: 'Grid3x3',
    aliases: ['beat snap', 'divisor', 'grid', 'subdivision', 'quantize', '分度', '细分'],
    level: 'guided',
    args: { kind: 'number', label: ref(m.argSnapDiv), min: 1, max: 1920 },
    enabled: projectOpen,
    run(ctx, args) {
      const div = numberArg(args, 'div');
      if (div !== undefined && div >= 1) ctx.editor.getState().setSnap({ div: Math.round(clamp(div, 1, 1920)) });
    },
  },
  {
    id: 'snap.finer',
    title: m.cmdSnapFiner,
    category: 'Edit',
    icon: 'Plus',
    aliases: ['beat snap', 'finer', 'smaller grid'],
    level: 'guided',
    enabled: projectOpen,
    run(ctx) {
      const e = ctx.editor.getState();
      e.setSnap({ div: cycleSnap(e.snap.div, 1) });
    },
  },
  {
    id: 'snap.coarser',
    title: m.cmdSnapCoarser,
    category: 'Edit',
    icon: 'Minus',
    aliases: ['beat snap', 'coarser', 'bigger grid'],
    level: 'guided',
    enabled: projectOpen,
    run(ctx) {
      const e = ctx.editor.getState();
      e.setSnap({ div: cycleSnap(e.snap.div, -1) });
    },
  },
  {
    id: 'snap.toggleBeat',
    title: m.cmdSnapBeat,
    category: 'Edit',
    icon: 'Magnet',
    aliases: ['snap', 'beat snap', 'magnet', 'grid', '吸附'],
    level: 'guided',
    enabled: projectOpen,
    checked: (ctx) => ctx.editor.getState().snap.beatSnap,
    menus: [{ menu: 'app.edit', group: 'snap', order: 10 }],
    run(ctx) {
      const e = ctx.editor.getState();
      e.setSnap({ beatSnap: !e.snap.beatSnap });
    },
  },
  {
    id: 'snap.toggleX',
    title: m.cmdSnapX,
    category: 'Edit',
    icon: 'Magnet',
    aliases: ['snap x', 'x grid', 'vertical lines', 'magnet', '竖线'],
    level: 'guided',
    enabled: projectOpen,
    checked: (ctx) => ctx.editor.getState().snap.xSnap,
    menus: [{ menu: 'app.edit', group: 'snap', order: 20 }],
    run(ctx) {
      const e = ctx.editor.getState();
      e.setSnap({ xSnap: !e.snap.xSnap });
    },
  },
  {
    id: 'snap.setXGrid',
    title: m.cmdSnapXGrid,
    category: 'Edit',
    icon: 'Columns4',
    aliases: ['x grid', 'columns', 'vertical lines', '竖线数'],
    level: 'guided',
    args: { kind: 'number', label: ref(m.argXGrid), min: 1, max: 1000 },
    enabled: projectOpen,
    run(ctx, args) {
      const n = numberArg(args, 'xGrid');
      if (n !== undefined && n >= 1) ctx.editor.getState().setSnap({ xGrid: Math.round(clamp(n, 1, 1000)) });
    },
  },
];

function revealArgs(args: unknown): {
  readonly beat: BeatArg | null;
  readonly line: LineId | null;
  readonly notes: readonly NoteId[];
  readonly keys: readonly KeyId[];
} {
  const o = obj(args);
  const b = beatFromArg(o.beat);
  const ids = (v: unknown): number[] => (Array.isArray(v) ? v.filter((x): x is number => typeof x === 'number') : []);
  return {
    beat: b === null ? null : { w: b.w, n: b.n, d: b.d },
    line: typeof o.line === 'number' ? (o.line as LineId) : null,
    notes: ids(o.notes) as NoteId[],
    keys: ids(o.keys) as KeyId[],
  };
}

const navCommands: CommandDef[] = [
  {
    id: 'nav.goto',
    title: m.cmdGoto,
    category: 'Playback',
    icon: 'Crosshair',
    aliases: ['go to', 'jump', 'seek to', 'time', 'beat', 'marker', '跳转'],
    level: 'guided',
    enabled: projectOpen,
    menus: [{ menu: 'app.edit', group: 'navigate', order: 10 }],
    run(ctx) {
      ctx.ui.openPalette('@');
    },
  },
  {
    id: 'nav.reveal',
    title: m.cmdReveal,
    category: 'Playback',
    icon: 'ScanSearch',
    aliases: ['reveal', 'show in timeline'],
    // A navigation primitive of Guided flows too (palette `@`, Go to, Problems "Go", jump to a change): never asks
    // for the Full level.
    level: 'guided',
    enabled: projectOpen,
    run(ctx, args) {
      const store = ctx.store;
      if (store === null) return;
      const a = revealArgs(args);
      if (a.beat === null) {
        ctx.ui.openPalette('@');
        return;
      }
      const e = ctx.editor.getState();
      if (e.workspace !== 'chart') ctx.nav.setWorkspace('chart');
      if (a.line !== null && store.doc.lines.has(a.line)) e.setActiveLine(a.line);
      const notes = a.notes.filter((id) => store.doc.notes.has(id));
      const keys = a.keys.filter((id) => store.doc.keys.has(id));
      if (notes.length > 0 || keys.length > 0) e.select({ notes: new Set(notes), keys: new Set(keys) }, 'replace');
      const beat = Beats.make(a.beat.w, a.beat.n, a.beat.d);
      seekSec(ctx, tempoOf(store).secAt(beat), false);
      try {
        ctx.timeline?.reveal(Beats.toF64(beat), 'center');
      } catch {
        // Timeline not mounted yet: the playhead moved; the panel follows it when it mounts.
      }
    },
  },
  {
    id: 'nav.prevKey',
    title: m.cmdPrevKey,
    category: 'Motion',
    icon: 'ChevronLeft',
    aliases: ['previous keyframe', 'previous key', 'jump to key', '上一个关键帧'],
    level: 'guided',
    repeat: true,
    enabled: (ctx) => keyNavEnabled(ctx),
    run(ctx, args) {
      keyNav(ctx, args, -1);
    },
  },
  {
    id: 'nav.nextKey',
    title: m.cmdNextKey,
    category: 'Motion',
    icon: 'ChevronRight',
    aliases: ['next keyframe', 'next key', 'jump to key', '下一个关键帧'],
    level: 'guided',
    repeat: true,
    enabled: (ctx) => keyNavEnabled(ctx),
    run(ctx, args) {
      keyNav(ctx, args, 1);
    },
  },
  {
    id: 'nav.prevNote',
    title: m.cmdPrevNote,
    category: 'Notes',
    icon: 'ArrowDown',
    aliases: ['previous note', 'jump to note'],
    level: 'guided',
    repeat: true,
    enabled: (ctx) => noteNavEnabled(ctx),
    run(ctx) {
      const line = ctx.editor.getState().activeLine;
      if (line !== null) seekNeighbour(ctx, noteBeats(ctx, line), -1, ref(m.toastNoNoteBefore));
    },
  },
  {
    id: 'nav.nextNote',
    title: m.cmdNextNote,
    category: 'Notes',
    icon: 'ArrowUp',
    aliases: ['next note', 'jump to note'],
    level: 'guided',
    repeat: true,
    enabled: (ctx) => noteNavEnabled(ctx),
    run(ctx) {
      const line = ctx.editor.getState().activeLine;
      if (line !== null) seekNeighbour(ctx, noteBeats(ctx, line), 1, ref(m.toastNoNoteAfter));
    },
  },
];

function noteNavEnabled(ctx: CommandContext): Enabled {
  if (ctx.store === null) return ref(m.reasonNoProject);
  return ctx.editor.getState().activeLine === null ? ref(m.reasonNoActiveLine) : true;
}

function keyNavEnabled(ctx: CommandContext): Enabled {
  if (ctx.store === null) return ref(m.reasonNoProject);
  const e = ctx.editor.getState();
  return e.focusedColumn === null && e.activeLine === null ? ref(m.reasonNoFocusedColumn) : true;
}

/** Previous / next keyframe of the focused column, or of every animated property of the active line (`any`). */
function keyNav(ctx: CommandContext, args: unknown, dir: 1 | -1): void {
  const store = ctx.store;
  if (store === null) return;
  const e = ctx.editor.getState();
  const any = obj(args).any === true;
  let tracks: TrackId[] = [];
  const col = e.focusedColumn;
  if (!any && col !== null) {
    const t = store.idx.track(col.line, col.prop, col.layer);
    if (t !== undefined) tracks = [t];
  } else if (e.activeLine !== null) {
    tracks = tracksOfLine(ctx, e.activeLine);
  }
  seekNeighbour(ctx, keyBeatsOf(ctx, tracks), dir, ref(dir === 1 ? m.toastNoKeyAfter : m.toastNoKeyBefore));
}

const helpCommands: CommandDef[] = [
  {
    id: 'help.palette',
    title: m.cmdHelpPalette,
    category: 'Help',
    icon: 'CircleHelp',
    aliases: ['help', 'search help', 'glossary', 'how to', '帮助'],
    level: 'guided',
    menus: [{ menu: 'app.help', group: 'find', order: 20 }],
    run(ctx) {
      ctx.ui.openPalette('?');
    },
  },
  {
    id: 'help.cheatSheet',
    title: m.cmdCheatSheet,
    category: 'Help',
    icon: 'Keyboard',
    aliases: ['shortcuts', 'keys', 'hotkeys', 'cheat sheet', 'keyboard', '快捷键'],
    level: 'guided',
    hint: 3,
    menus: [{ menu: 'app.help', group: 'keys', order: 10 }],
    run(ctx) {
      if (isShellUiService(ctx.ui)) ctx.ui.setCheatSheet(true);
      else ctx.ui.openPalette('?');
    },
  },
];

/** Every command of the shell module. */
export const SHELL_COMMANDS: readonly CommandDef[] = [
  ...appCommands,
  ...editCommands,
  ...viewCommands,
  ...playbackCommands,
  ...snapCommands,
  ...navCommands,
  ...helpCommands,
];

// ---------------------------------------------------------------------------------------------------------------
// Key bindings (DESIGN.md §8.2 Default, §8.3 RPE)
// ---------------------------------------------------------------------------------------------------------------

const kb = (chord: string, command: string, extra: Partial<KeyBinding> = {}): KeyBinding => ({
  chord,
  command,
  ...extra,
});
const args = (a: Json): Partial<KeyBinding> => ({ args: a });

/** Bindings shared by both presets. */
const SHARED: readonly KeyBinding[] = [
  ...FOCUS_REGION_KEYBINDINGS.default,
  kb('Ctrl+KeyZ', 'edit.undo'),
  kb('Ctrl+Shift+KeyZ', 'edit.redo'),
  kb('Ctrl+KeyY', 'edit.redo'),
  kb('F2', 'app.workspace', args({ workspace: 'timing' })),
  kb('F3', 'app.workspace', args({ workspace: 'chart' })),
  kb('F4', 'app.workspace', args({ workspace: 'publish' })),
  kb('F8', 'view.miniPreview'),
  kb('F9', 'view.drawer'),
  kb('char:?', 'help.cheatSheet'),
  kb('Space', 'playback.toggle'),
  kb('Shift+Space', 'playback.playFromStart'),
  kb('hold:KeyT', 'playback.holdReturn'),
  kb('hold:KeyU', 'playback.holdStay'),
  kb('Shift+Comma', 'playback.rateDown'),
  kb('Shift+Period', 'playback.rateUp'),
  kb('KeyN', 'playback.metronome'),
  kb('Alt+KeyL', 'playback.loop', { altFree: null }),
  kb('ArrowUp', 'playback.seekForward', { when: '!sel.any' }),
  kb('ArrowDown', 'playback.seekBack', { when: '!sel.any' }),
  kb('PageUp', 'playback.seekBarForward'),
  kb('PageDown', 'playback.seekBarBack'),
  kb('Home', 'playback.seekStart'),
  kb('End', 'playback.seekEnd'),
  kb('Shift+Home', 'playback.seekSelectionStart'),
  kb('Shift+End', 'playback.seekSelectionEnd'),
  kb('Comma', 'nav.prevKey'),
  kb('Period', 'nav.nextKey'),
  kb('Shift+Comma', 'nav.prevKey', { args: { any: true }, when: 'focus.motion || focus.curve' }),
  kb('Shift+Period', 'nav.nextKey', { args: { any: true }, when: 'focus.motion || focus.curve' }),
  // DESIGN.md §5.9 / §8.2 "Previous / next note | Alt+Up / Alt+Down (Alt-free: Shift+PageUp / Shift+PageDown)".
  kb('Alt+ArrowUp', 'nav.prevNote', { altFree: 'Shift+PageUp' }),
  kb('Alt+ArrowDown', 'nav.nextNote', { altFree: 'Shift+PageDown' }),
  kb('KeyF', 'view.frameSelection'),
  kb('Digit0', 'view.resetView'),
  kb('Ctrl+Equal', 'view.zoomIn'),
  kb('Ctrl+NumpadAdd', 'view.zoomIn'),
  kb('Ctrl+Minus', 'view.zoomOut'),
  kb('Ctrl+NumpadSubtract', 'view.zoomOut'),
  kb('Alt+KeyN', 'view.motionColumns', { altFree: null }),
  ...SNAP_DIGIT_DIVS.map((div, i) => kb(`Shift+Digit${String(i + 1)}`, 'snap.setDiv', args({ div }))),
  kb('Shift+KeyG', 'snap.toggleBeat'),
  kb('Shift+KeyX', 'snap.toggleX'),
];

export const SHELL_KEYBINDINGS: Readonly<Record<KeymapPresetId, readonly KeyBinding[]>> = {
  default: [...SHARED, kb('Ctrl+KeyK', 'app.palette'), kb('Ctrl+KeyP', 'app.palette'), kb('Ctrl+KeyG', 'nav.goto')],
  rpe: [
    ...SHARED,
    kb('Ctrl+KeyP', 'app.palette'),
    kb('Ctrl+Shift+KeyG', 'nav.goto'),
    // NAV-06..09: play maximised / stop and return / stop and stay ("Leap") / play from chart start.
    kb('KeyI', 'playback.playMaximized'),
    kb('KeyO', 'playback.stopReturn'),
    kb('KeyP', 'playback.stopStay'),
    kb('BracketLeft', 'playback.playFromStart'),
    // NAV-12: 0.5x / 0.75x / 1.0x.
    kb('Ctrl+KeyJ', 'playback.setRate', args({ rate: 0.5 })),
    kb('Ctrl+KeyK', 'playback.setRate', args({ rate: 0.75 })),
    kb('Ctrl+KeyL', 'playback.setRate', { args: { rate: 1 }, mac: null }),
    // NAV-04: snap the playhead to the grid.
    kb('Ctrl+KeyH', 'playback.snapPlayhead', { mac: null }),
    // VIEW-05: reset the lane X pan (Cmd+M minimises the window on macOS: menu and palette remain).
    kb('Ctrl+KeyM', 'view.resetLanePan', { mac: null }),
    // Nothing selected: seek by 0.1 s.
    kb('ArrowLeft', 'playback.seekSeconds', { args: { sec: -0.1 }, when: '!sel.any' }),
    kb('ArrowRight', 'playback.seekSeconds', { args: { sec: 0.1 }, when: '!sel.any' }),
    // NAV-14: jump to the selection start / end (unreliable on Chrome; Shift+Home / Shift+End also work).
    kb('Alt+KeyF', 'playback.seekSelectionStart', { altFree: null }),
    kb('Alt+KeyG', 'playback.seekSelectionEnd', { altFree: null }),
  ],
};
